import React from "react";
import {
  Button,
  Checkbox,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  ControlledCustomSelect,
  CustomFormLabel,
} from "../../../../../../../components/formsComponents";
import { LabelCard } from "../../../../../../../components/LabelCard/LabelCard";
import { StandardTableActions } from "../../../../../../../components/StandardTableActions/StandardTableActions";
import produce from "immer";
import { IAutomobilePolicyChangeProps } from "./AutomobilePolicyChange.types";
import { useAutomobilePolicyDriverInfo } from "../../hooks/useAutomobilePolicyDriverInfo";
import { IAutomobileDriverInformation } from "../../../../../../../redux/automobileDriver/automobileDriver.types";
import { AutomobileDriverRightPanel } from "../../AutomobileDriver/AutomobileDriverInformation/AutomobileDriverRightPanel";
import { ConfirmDialog } from "../../../../../../../components/Dialogs/ConfirmDialog";
import { AddOutlined } from "@mui/icons-material";

export const DriverInformation: React.FC<IAutomobilePolicyChangeProps> = ({
  values,
  setValues,
  errors,
  handleChange,
  setFieldValue,
  customerId,
  customerPolicyId,
  isStatusCompleted,
  autoSaveData = () => {},
}) => {
  const [open, setOpen] = React.useState<{
    type: "create" | "edit" | "view";
    index?: number;
    data?: IAutomobileDriverInformation;
  } | null>(null);
  const { driverDropDown, refreshList } = useAutomobilePolicyDriverInfo(customerPolicyId);

  const handleCheckBoxChange = (value: string) => {
    setFieldValue("driver_change.type", value);
  };

  const handleSaveSuccess = (driver: IAutomobileDriverInformation) => {
    const newValues = produce(values, (draftValues) => {
      const updatedData = {
        ...driver,
        customer_policy_id: customerPolicyId ?? null,
        customer_id: customerId ? Number(customerId) : null,
      };
      if (open && open.type === "edit" && open.index !== undefined) {
        draftValues.driver_change.records[open.index] = updatedData;
      } else {
        draftValues.driver_change.records.push(updatedData);
      }
    });
    autoSaveData(newValues);
    setValues(newValues);
    setOpen(null);
    refreshList();
  };

  const handleCreateEdit = (index?: number) => () => {
    if (isStatusCompleted) {
      return;
    }
    setOpen({
      type: index !== undefined ? "edit" : "create",
      index: index,
      data:
        index !== undefined ? values.driver_change.records[index] : undefined,
    });
  };

  const handleView = (index: number) => () => {
    setOpen({
      type: "view",
      index: index,
      data: values.driver_change.records[index],
    });
  };

  const [deleteConfirm, setDeleteConfirm] = React.useState<number | null>(null);
  const handleDelete = (index: number) => () => {
    setDeleteConfirm(index);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm === null) {
      return;
    }
    const newValues = produce(values, (draftValues) => {
      draftValues.driver_change.records.splice(deleteConfirm, 1);
    });
    setValues(newValues);
    autoSaveData(newValues);
    setDeleteConfirm(null);
  };

  return (
    <>
      <LabelCard heading="Driver Change" />
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} lg={2.4}>
          <Stack direction={"row"} alignItems={"center"}>
            <Checkbox
              checked={values.driver_change.type === "Add"}
              onChange={(e) => {
                handleCheckBoxChange(e.target.checked ? "Add" : "");
              }}
            />
            <Typography>Add</Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} lg={2.4}>
          <Stack direction={"row"} alignItems={"center"}>
            <Checkbox
              checked={values.driver_change.type === "Delete"}
              onChange={(e) => {
                handleCheckBoxChange(e.target.checked ? "Delete" : "");
              }}
            />
            <Typography>Delete</Typography>
          </Stack>
        </Grid>
        {/* <Grid item xs={12} lg={2.4}>
          <Stack direction={"row"} alignItems={"center"}>
            <Checkbox
              checked={values.driver_change.type === "Change"}
              onChange={(e) => {
                handleCheckBoxChange(e.target.checked ? "Change" : "");
              }}
            />
            <Typography>Substitution</Typography>
          </Stack>
        </Grid> */}
      </Grid>

      {values.driver_change.type &&
        ["Add", "Change"].includes(values.driver_change.type) && (
          <>
            <Grid container spacing={3}>
              {values.driver_change.type === "Change" && (
                <Grid item xs={12} md={6}>
                  <CustomFormLabel>Delete Driver Info</CustomFormLabel>
                  <ControlledCustomSelect
                    variant="outlined"
                    size="small"
                    type="string"
                    name="driver_change.delete_driver"
                    fullWidth
                    displayEmpty
                    value={Number(values.driver_change.delete_driver)}
                    onChange={handleChange}
                    placeholder="Select One"
                    options={driverDropDown}
                  ></ControlledCustomSelect>
                </Grid>
              )}
              <Grid item xs={12}>
                <Stack direction={"row"} justifyContent={"end"}>
                  <Button
                    variant="contained"
                    disabled={isStatusCompleted}
                    onClick={handleCreateEdit()}
                    startIcon={<AddOutlined />}
                  >
                    New
                  </Button>
                </Stack>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>View</TableCell>
                        <TableCell>#</TableCell>
                        <TableCell>Driver Name</TableCell>
                        <TableCell>Address</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {values.driver_change.records.map((item, index) => {
                        return (
                          <TableRow key={index}>
                            <TableCell sx={{ display: "flex" }}>
                              <StandardTableActions
                                onViewClick={handleView(index)}
                                onEditClick={
                                  !isStatusCompleted
                                    ? handleCreateEdit(index)
                                    : undefined
                                }
                                // onDeleteClick={
                                //   !isStatusCompleted
                                //     ? handleDelete(index)
                                //     : undefined
                                // }
                              />
                            </TableCell>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>{item.driver_name}</TableCell>
                            <TableCell>{item.license_number}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </>
        )}

      {values.driver_change.type &&
        ["Delete"].includes(values.driver_change.type) && (
          <Grid container>
            <Grid item xs={12} md={6}>
              <CustomFormLabel>Driver Info</CustomFormLabel>
              <ControlledCustomSelect
                variant="outlined"
                size="small"
                type="string"
                name="driver_change.delete_driver"
                fullWidth
                displayEmpty
                value={Number(values.driver_change.delete_driver)}
                onChange={handleChange}
                placeholder="Select One"
                options={driverDropDown}
              ></ControlledCustomSelect>
            </Grid>
          </Grid>
        )}

      {open && (
        <AutomobileDriverRightPanel
          open={true}
          readonly={open.type === "view"}
          customerId={customerId}
          customerPolicyId={customerPolicyId}
          driverNumber={open.data ? open.data.driver_number as any : undefined}
          onSaveSuccess={handleSaveSuccess}
          staticStatusValue="ACTIVE"
          created_from="Policy_Change"
          onClose={() => setOpen(null)}
          //initialData={open.data}
         // disableApiCalls
        />
      )}

      {deleteConfirm !== null && (
        <ConfirmDialog
          open
          content="Are you sure you want to delete this record?"
          onClose={() => setDeleteConfirm(null)}
          onConfrim={handleDeleteConfirm}
        />
      )}
    </>
  );
};
